import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PenSquare, Save, X, Book, Plus, Loader2 } from 'lucide-react';
import { articleService }  from '../services/api';

const ArticleCreator = () => {
  const [articles, setArticles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentArticle, setCurrentArticle] = useState({
    title: '',
    content: '',
    category: '',
    tags: []
  });

  // Fetch articles on component mount
  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setIsLoading(true);
      const response = await articleService.getArticles();
      setArticles(response.articles || []);
    } catch (err) {
      setError('Failed to load articles');
      console.error('Error fetching articles:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentArticle(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous errors when user starts typing
    setError(null);
  };

  const handleTagInput = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim();
      if (!currentArticle.tags.includes(newTag)) {
        setCurrentArticle(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      e.target.value = '';
    }
  };

  const removeTag = (tagToRemove) => {
    setCurrentArticle(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!currentArticle.title || !currentArticle.content || !currentArticle.category) {
      setError('Please fill in all required fields (title, content, and category)');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Save article via API
      const response = await articleService.createArticle({
        title: currentArticle.title,
        content: currentArticle.content,
        category: currentArticle.category,
        tags: currentArticle.tags
      });

      // Reset form and refresh articles list
      setCurrentArticle({ title: '', content: '', category: '', tags: [] });
      setIsEditing(false);
      await fetchArticles();

    } catch (err) {
      setError('Failed to save article. Please try again.');
      console.error('Error saving article:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentArticle({ title: '', content: '', category: '', tags: [] });
    setIsEditing(false);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Article Creation/Editing Form */}
      {isEditing ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenSquare className="h-6 w-6" />
              Create New Article
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={currentArticle.title}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter article title"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={currentArticle.category}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  <option value="">Select a category</option>
                  <option value="disabilityresources">Disability Resources</option>
                  <option value="housingassets">Housing & Assets</option>
                  <option value="financesmoney">Finances & Money</option>
                  <option value="governmentsocialprograms">Government & Social Programs </option>
                  <option value="buildingsupport">Building Support </option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tags</label>
                <input
                  type="text"
                  onKeyPress={handleTagInput}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="Type tag and press Enter"
                  disabled={isLoading}
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {currentArticle.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                        disabled={isLoading}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="content"
                  value={currentArticle.content}
                  onChange={handleInputChange}
                  rows="10"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="Write your article content here..."
                  disabled={isLoading}
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleCancel}
                  className="flex items-center px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={isLoading}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {isLoading ? 'Saving...' : 'Save Article'}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <button
          onClick={() => setIsEditing(true)}
          className="w-full p-4 border-2 border-dashed rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-400 flex items-center justify-center"
          disabled={isLoading}
        >
          <Plus className="h-6 w-6 mr-2" />
          Create New Article
        </button>
      )}

      {/* Articles List */}
      <div className="space-y-4 mt-8">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Book className="h-6 w-6" />
          Articles
        </h2>
        {isLoading && !articles.length ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-2" />
            <p className="text-gray-500">Loading articles...</p>
          </div>
        ) : articles.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No articles created yet.</p>
        ) : (
          articles.map(article => (
            <Card key={article.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle>{article.title}</CardTitle>
                <div className="text-sm text-gray-500">
                  {new Date(article.dateCreated).toLocaleDateString()}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm">
                    {article.category}
                  </span>
                  {article.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-600 line-clamp-3">{article.content}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ArticleCreator;